import React, { useCallback, useEffect, useMemo } from 'react';
import BaseModalComponent, { StyledBaseModal } from '../BaseModal.component';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import SpinnerComponent, { StyledSpinnerView } from '../../Spinner.component';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import FormLayoutComponent from '../../Form/FormLayout.component';
import InputComponent from '../../Input/Input.component';
import {
  maxLength,
  minLength,
  required,
} from '../../../services/validator.service';
import ButtonComponent, { StyledButton } from '../../Button.component';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { Transition } from 'react-transition-group';
import { getInternshipSelected } from '../../../redux/Internship/Internship.slice';
import { InternshipRequest } from '../../../redux/Internship/Internship.type';
import {
  createInternshipThunk,
  updateInternshipThunk,
} from '../../../redux/Internship/Internship.thunk';
import DatepickerModalComponent from '../DatepickerModal.component';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../constants/date.contant';
import SelectComponent from '../../Input/Select.component';
import { Job } from '../../../redux/Job/Job.type';
import { Employee } from '../../../redux/Employee/Employee.type';
import UserBadgeComponent from '../../UserBadge.component';
import useJobsHook from '../../../hooks/useJobs.hook';
import useEmployeeHook from '../../../hooks/useEmployee.hook';
import { getRegionListThunk } from '../../../redux/Region/Region.thunk';
import { Region } from '../../../redux/Region/Region.type';
import useAuthHook from '../../../hooks/useAuth.hook';
import TextareaComponent from '../../Input/Textarea.component';
import ApiWrapper from '../../../hooks/useApi.hook';
import CardComponent, { StyledCard } from '../../Card.component';
import ModalAttachBlockComponent from '../../Blocks/ModalAttachBlock.component';
import DateRangeModalComponent from '../DateRangeModal.component';
import { textAriasMaxLength } from '../../../constants/textArias.constant';
import EmployeeSelect from '../../Input/EmployeeSelect.component';
import RecruiterSelect from '../../Input/RecruiterSelect.component';
import useFormHook from '../../../hooks/useForm.hook';
import {
  getInternshipStatusControlThunk
} from '../../../redux/ControlPanel/InternshipStatusControl/InternshipStatusControl.thunk';
import { InternshipStatus } from '../../../redux/InternshipStatus/InternshipStatus.type';
import { InternshipStatusResponse } from '../../../redux/InternshipStatus/InternshipStatus.service';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from '../../../utils/storage';
import { AttachEditState } from '../../../redux/Attachment/Attachment.type';

const INTERNSHIP_TYPE = {
  GROUP: 'GROUP',
  INDIVIDUAL: 'INDIVIDUAL',
};

const INTERNSHIP_FORMAT = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
};

export interface InternshipEditModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  handleClose?: () => void;
}

const StyledPersonEditModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;
const StyledSelectOption = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StyledInternshipEditModalStack = styled.div`
  display: grid;
  align-items: center;
  padding: 10px 0;
  gap: 15px;
`;

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

const StyledAttachModal = styled.div`
  display: flex;
    
  animation: ${({ theme }) => theme.animations.slide.modal} 0.2s ease-in-out
    forwards;

  ${StyledCard} {
    width: 300px;
    height: auto;
  }
`;

const StyledInternshipEditModalBody = styled.div``;
const StyledInternshipEditModal = styled.div`
  ${StyledBaseModal} {
    width: 600px;
  }

  ${StyledSpinnerView} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => theme.colors.CARD};
    border-radius: 8px;
  }
`;

const dateNow = dayjs().valueOf();

const InternshipEditModalComponent = ({
  handleClose,
}: InternshipEditModalProps) => {
  const theme = useTheme() as any;
  const dispatch = useAppDispatch();
  const record = useAppSelector(getInternshipSelected);
  const { t } = useTranslation();
  const { openModal, closeModal } = useModalManager();
  const { getSelectList: getJobsList } = useJobsHook();
  const { getSelectList: getEmployeeList } = useEmployeeHook();
  const { user } = useAuthHook();
  const currentDate = dayjs().unix() * 1000;

  const savedEntity = record.item && record.item.id ? `editInternship${record?.item?.id}` : 'newInternship';
  const savedData = getLocalStorage(savedEntity) || '';
  const parsedSavedData = savedData && savedData.length ? JSON.parse(savedData) : {};

  const { form, registerField, validate, setFormValue, handleFormChange } =
    useFormHook({
      name: '',
      testing_at: dateNow,
      dateRange: { start: currentDate, end: currentDate },
      jobs: [],
      mentors: [],
      techSpecialists: [],
      regions: [],
      type: {
        value: INTERNSHIP_TYPE.GROUP,
        label: t(`internship.status.${INTERNSHIP_TYPE.GROUP}`),
      },
      comment: '',
      attachments: [],
      links: [],
    });

  useEffect(() => {
    const item = record?.item || parsedSavedData;
    const id = item?.id;
    const name = parsedSavedData.name || item?.name;
    const testingAt = (parsedSavedData.testingAt || item?.testingAt) * 1000 || dateNow;
    const startAt = (parsedSavedData.startAt || item?.startAt) * 1000 || currentDate;
    const endAt = (parsedSavedData.endAt || item?.endAt) * 1000 || currentDate;
    const regions = parsedSavedData.regions || item?.regions || [];
    const status = parsedSavedData.status || item?.status;
    const recruiter = parsedSavedData.recruiter || item?.recruiter || user;
    const format = parsedSavedData.format?.value || item?.format;
    const jobs = parsedSavedData.jobs || item?.jobs || [];
    const mentors = parsedSavedData.mentors || item?.mentors || [];
    const techSpecialists = parsedSavedData.techSpecialists || item?.techSpecialists || [];
    const comment = parsedSavedData.comment || item?.comment;
    const attachments = parsedSavedData.attachments || item?.attachments || [];

    const newForm = {
      id,
      name,
      testing_at: testingAt,
      start_at: startAt,
      end_at: endAt,
      dateRange: {
        start: startAt,
        end: endAt,
      },
      regions:
        regions?.map((region: Region) => ({
          value: region.id,
          label: region?.name,
        })),
      status: status && {
        value: status?.id,
        label: status?.name,
      },
      type: {
        value: INTERNSHIP_TYPE.GROUP,
        label: t(`internship.status.${INTERNSHIP_TYPE.GROUP}`),
      },
      recruiter: {
        value: recruiter?.id,
        label: (
          <StyledSelectOption>
            <UserBadgeComponent info={recruiter} />
          </StyledSelectOption>
        ),
        available: recruiter?.available,
      },
      format: format && {
        value: format,
        label: format ? t(`internship.format.${format}`) : '',
      },
      jobs:
        jobs?.map((job: Job) => ({
          value: job.id,
          label: job.name,
        })),
      mentors:
        mentors?.map((mentor: Employee) => ({
          value: mentor.id,
          label: (
            <StyledSelectOption>
              <UserBadgeComponent info={mentor} />
            </StyledSelectOption>
          ),
          available: mentor.available,
        })),
      techSpecialists:
        techSpecialists?.map((techSpecialist: Employee) => ({
          value: techSpecialist.id,
          label: (
            <StyledSelectOption>
              <UserBadgeComponent info={techSpecialist} />
            </StyledSelectOption>
          ),
          available: techSpecialist.available,
        })),
      comment,
      attachments,
    };

    setFormValue(newForm);
  }, [record]);

  const regionListLoadOptions = useCallback(
    async (search: string, { page }: { page: number }) => {
      const {
        payload: { data, pagination },
      } = await dispatch(getRegionListThunk({ page, search }));
      return {
        options: data.map((item: Region) => ({
          value: item.id,
          label: item.name,
        })),
        hasMore: pagination.current_page < pagination.last_page,
        additional: {
          page: pagination.current_page + 1,
        },
      };
    },
    [],
  );

  const statusListLoadOptions = useCallback(
    async (search: string, { page }: { page: number }) => {
      const result = await dispatch(getInternshipStatusControlThunk({ page, q: search }));
      const { data, pagination } = result.payload as InternshipStatusResponse;

      return {
        options: data.map((item: InternshipStatus) => ({
          value: item.id,
          label: item.name,
        })),
        hasMore: pagination.current_page < pagination.last_page,
        additional: {
          page: pagination.current_page + 1,
        },
      };
    },
    [],
  );

  const saveResult = useCallback(async () => {
    if (!validate()) return;

    const data = {
      id: record?.item?.id || undefined,
      name: form.name,
      regions: form.regions?.map((region: Record<string, any>) => region.value),
      jobs: form.jobs?.map((job: Record<string, any>) => job.value),
      mentors: form.mentors?.map((mentor: Record<string, any>) => mentor.value),
      testing_at: dayjs(form.testing_at).format('YYYY-MM-DD'),
      start_at: dayjs(form.dateRange.start).format('YYYY-MM-DD'),
      end_at: dayjs(form.dateRange.end).format('YYYY-MM-DD'),
      statusId: form.status?.value,
      format: form.format?.value,
      type: form.type.value,
      recruiterId: form.recruiter?.value,
      techSpecialists: form.techSpecialists?.map(
        (techSpecialist: { value: any }) => techSpecialist.value,
      ),
      attachments: form.attachments,
      links: form.links,
      comment: form.comment,
    };

    ApiWrapper(
      data.id
        ? updateInternshipThunk(data as InternshipRequest)
        : createInternshipThunk(data as InternshipRequest),
    ).then(() => {
      deleteLocalStorage(savedEntity);
      handleClose && handleClose();
      closeModal();
    });
  }, [form]);

  const handleFormSave = (data: {[key: string]: any}) => {
    const attrKey = Object.keys(data)[0];
    const attrValue = Object.values(data)[0];

    const savedEntity = record.item && record.item.id ? `editInternship${record?.item?.id}` : 'newInternship';
    const savedData = getLocalStorage(savedEntity) || '';
    const parsedSavedData = savedData && savedData.length ? JSON.parse(savedData) : {};

    if (!attrValue && (attrKey in parsedSavedData)) {
      delete parsedSavedData[attrKey];
      setLocalStorage(savedEntity, JSON.stringify(parsedSavedData));
      return;
    }
    if (!attrValue) return;

    let value: { id: number, name: string, code: number | string } | { label: string, value: string | number} | string | number = {
      id: attrValue?.value,
      name: attrValue?.label,
      code: attrValue?.code,
    }

    if (['hr', 'recruiter'].includes(attrKey)) {
      value = attrValue?.label?.props?.children?.props?.info;
    }

    if (['name', 'attachments', 'comment', 'format'].includes(attrKey)) {
      value = attrValue;
    }

    if (['startAt', 'endAt', 'testingAt'].includes(attrKey)) {
      value = attrValue as number;
      value = value / 1000;
    }

    if (['type'].includes(attrKey)) {
      value = attrValue.value;
    }

    if (['jobs', 'regions'].includes(attrKey)) {
      value = attrValue?.map((item: {value: number, label: string}) => ({ id: item.value, name: item.label }));
    }

    if (['mentors', 'techSpecialists'].includes(attrKey)) {
      value = attrValue?.map((item: { label: any, value: number, available?: boolean }) => item?.label?.props?.children?.props?.info);
    }

    const newData = JSON.stringify({
      ...parsedSavedData,
      [attrKey]: value,
    });

    if (newData) setLocalStorage(savedEntity, newData);
  }

  const renderHeader = useMemo(() => {
    if (!record.item || (record.item && !record.item.id))
      return record.loading ? t('Loading') : t('New internship');
    return t('Internship');
  }, [record]);

  const attachmentsSidebar = useMemo(
    () => (
      <StyledAttachModal>
        <CardComponent isLoading={record.loading}>
          <ModalAttachBlockComponent
            {...registerField('attachments')}
            value={form.attachments || []}
            onChangeAttachments={(attachments: AttachEditState[]) => {
              handleFormChange({ attachments });
              handleFormSave({ attachments: attachments.filter((item: AttachEditState) => !item.file) });
            }}
          />
        </CardComponent>
      </StyledAttachModal>
    ),
    [record, form, form.attachments],
  );

  const renderFooter = useMemo(() => {
    return (
      <StyledPersonEditModalFooter>
        <ButtonComponent
          appearance="primary"
          disabled={record.loading}
          handleClick={saveResult}
        >
          {t('Next')}
        </ButtonComponent>

        <ButtonComponent handleClick={() => {
          closeModal();
          deleteLocalStorage(savedEntity);
        }}>
          {t('Cancel')}
        </ButtonComponent>
      </StyledPersonEditModalFooter>
    );
  }, [record, form]);

  const selectFormatOptions = useMemo(
    () =>
      Object.values(INTERNSHIP_FORMAT).map(value => ({
        value,
        label: t(`internship.format.${value}`),
      })),
    [],
  );

  const formatDateRange = useMemo(
    () =>
      `${dayjs(form.dateRange?.start).format(DATE_FORMAT)} - ${dayjs(
        form.dateRange?.end,
      ).format(DATE_FORMAT)}`,
    [form],
  );

  const renderContent = useMemo(
    () => (
      <StyledInternshipEditModalBody>
        <FormLayoutComponent>
          <StyledInternshipEditModalStack>
            <InputComponent
              {...registerField('name', {
                validations: [
                  required,
                  minLength(2),
                  maxLength(textAriasMaxLength.EXTENDED),
                ],
              })}
              value={form.name}
              label={t('Title') + '*'}
              placeholder={t('Enter title')}
              onChange={(name: string) => {
                handleFormChange({ name });
                handleFormSave({ name });
              }}
            />
            <StyledRow>
              <SelectComponent
                {...registerField('regions', {
                  validations: [required],
                })}
                value={form.regions}
                label={t('Region') + '*'}
                placeholder={t('Choose regions')}
                isMulti
                loadOptions={regionListLoadOptions}
                onChangeOption={(regions: { label: string, value: number }) => {
                  handleFormChange({ regions });
                  handleFormSave({ regions });
                }}
              />

              <SelectComponent
                {...registerField('status', {
                  validations: [required],
                })}
                value={form.status}
                label={t('Status') + '*'}
                placeholder={t('Choose status')}
                loadOptions={statusListLoadOptions}
                onChangeOption={(status: { label: string, value: number }) => {
                  handleFormChange({ status });
                  handleFormSave({ status });
                }}
              />

              <SelectComponent
                {...registerField('format', {
                  validations: [required],
                })}
                value={form.format}
                label={t('Format') + '*'}
                options={selectFormatOptions}
                placeholder={t('Choose format')}
                onChangeOption={(format: { label: string, value: string }) => {
                  handleFormChange({ format });
                  handleFormSave({ format });
                }}
              />
            </StyledRow>

            <StyledRow>
              <InputComponent
                {...registerField('testing_at', {
                  validations: [required],
                })}
                value={
                  form.testing_at
                    ? dayjs(form.testing_at).format(DATE_FORMAT)
                    : undefined
                }
                label={t('Date testing')}
                onClick={() =>
                  openModal(DatepickerModalComponent, {
                    withTime: false,
                    defaultDate: form.testing_at ? form.testing_at : dateNow,
                    handleChange: (date: number) => {
                      handleFormChange({ testing_at: date });
                      handleFormSave({ testingAt: date });
                    }
                  })
                }
              />

              <InputComponent
                {...registerField('dateRange')}
                label={t('Date all')}
                onClick={() =>
                  openModal(DateRangeModalComponent, {
                    dateRange: form.dateRange,
                    handleChange: (dateRange: { start: number, end: number }) => {
                      handleFormChange({ dateRange });
                      handleFormSave({ startAt: dateRange.start });
                      handleFormSave({ endAt: dateRange.end });
                    },
                    withoutDaysBefore: true,
                  })
                }
                value={formatDateRange}
              />
            </StyledRow>

            <StyledRow>
              <SelectComponent
                {...registerField('jobs')}
                value={form.jobs}
                label={t('Jobs')}
                isMulti
                isSearchable
                placeholder={t('Choose jobs')}
                loadOptions={getJobsList}
                onChangeOption={(jobs: { label: string, value: number }) => {
                  handleFormChange({ jobs });
                  handleFormSave({ jobs });
                }}
                selectedItemsLimit={3}
              />

              <SelectComponent
                {...registerField('mentors', {
                  validations: [required],
                })}
                value={form.mentors}
                label={t('Mentors') + '*'}
                isMulti
                isSearchable
                placeholder={t('Choose mentors')}
                loadOptions={getEmployeeList}
                onChangeOption={(mentors: { label: any, value: number, available?: boolean }) => {
                  handleFormChange({ mentors });
                  handleFormSave({ mentors });
                }}
              />
            </StyledRow>

            <RecruiterSelect
              {...registerField('recruiter', {
                validations: [required],
              })}
              value={form.recruiter}
              isRequired
              onChange={(recruiter: { label: any, value: number, available?: boolean }) => {
                handleFormChange({ recruiter });
                handleFormSave({ recruiter });
              }}
            />

            <EmployeeSelect
              {...registerField('techSpecialists')}
              label={t('Tech specialist')}
              placeholder={t('Choose tech specialists')}
              value={form.techSpecialists}
              onChangeOption={(techSpecialists: { label: any, value: number, available?: boolean }[]) => {
                handleFormChange({ techSpecialists });
                handleFormSave({ techSpecialists });
              }}
              isMulti
            />

            <TextareaComponent
              {...registerField('comment')}
              value={form.comment}
              label={t('Comment')}
              onChange={(comment: string) => {
                handleFormChange({ comment });
                handleFormSave({ comment });
              }}
              maxLength={textAriasMaxLength.EXTENDED}
            />
          </StyledInternshipEditModalStack>
        </FormLayoutComponent>
      </StyledInternshipEditModalBody>
    ),
    [form],
  );

  return (
    <StyledInternshipEditModal>
      <BaseModalComponent
        header={renderHeader}
        footer={renderFooter}
        leftBlock={attachmentsSidebar}
      >
        {renderContent}
        <Transition in={!!record.loading} unmountOnExit timeout={{ exit: 300 }}>
          {state => (
            <SpinnerComponent
              style={{
                ...theme.animations.reverseDefaultState,
                ...theme.animations.defaultSteps[state],
              }}
            />
          )}
        </Transition>
      </BaseModalComponent>
    </StyledInternshipEditModal>
  );
};

export default InternshipEditModalComponent;
